import { ctaDesc, ctaTitle } from "@/lib/seo";
// /checklist/$visatype — per visa-type hub (lists all scenarios)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getVisaType } from "@/data/visa-nationalities";
import { CATEGORIES } from "@/data/visa-types";
import { SCENARIOS, getChecklist } from "@/data/visa-checklists";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema } from "@/lib/aeo";
import { TrustLayer } from "@/components/TrustLayer";
import { FileCheck2, Phone, MessageCircle, ArrowRight, ChevronRight } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/checklist/$visatype/")({
  loader: ({ params }) => {
    if (!getVisaType(params.visatype)) throw notFound();
    return { id: params.visatype };
  },
  head: ({ params }) => {
    const v = getVisaType(params.visatype);
    if (!v) return { meta: [{ title: ctaTitle("ไม่พบประเภทวีซ่า", "/checklist/$visatype", "th")}] };
    const url = `${SITE}/checklist/${v.id}`;
    const title = `Checklist เอกสาร ${v.nameTh} (${v.code}) ทุกสถานการณ์ | NYC`;
    const desc = `รวม Checklist ${v.nameTh}: ยื่นครั้งแรก ต่ออายุ เปลี่ยนนายจ้าง ครอบครัวติดตาม Work Permit เปลี่ยนประเภท — เอกสารครบ เสร็จ ${v.processingTime} — สอบถามฟรี ไม่มีข้อผูกมัด`;
    return {
      meta: [
        { title: ctaTitle(title, "/checklist/$visatype", "th") }, { name: "description", content: ctaDesc(desc, "/checklist/$visatype", "th")},
        { property: "og:title", content: ctaTitle(title, "/checklist/$visatype", "th")}, { property: "og:description", content: ctaDesc(desc, "/checklist/$visatype", "th")},
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc,
              hasPart: SCENARIOS.map((s) => ({
                "@type": "WebPage", name: `${v.nameTh} · ${s.shortTh}`, url: `${url}/${s.slug}`,
              })),
            }),
          },
          howToSchema({
            name: `วิธีเตรียมเอกสาร ${v.nameTh}`,
            description: `ขั้นตอนเตรียม Checklist เอกสาร ${v.nameTh} ทุกสถานการณ์ — ยื่นครั้งแรก ต่ออายุ เปลี่ยนนายจ้าง`,
            totalTime: "P7D",
            estimatedCost: { currency: "THB", value: "500" },
            inLanguage: "th",
            steps: [
              { name: "เลือกสถานการณ์", text: `เลือกจาก ${SCENARIOS.length} สถานการณ์ (ยื่นใหม่/ต่ออายุ/เปลี่ยนนายจ้าง/ครอบครัวติดตาม/เปลี่ยนประเภท)`, url },
              { name: "ดาวน์โหลด Checklist", text: `รับ PDF Checklist เฉพาะ ${v.nameTh} ตามสถานการณ์`, url },
              { name: "เตรียมและแปลเอกสาร", text: "ส่งเอกสารแปลรับรองนักแปลขึ้นทะเบียน + Apostille หากต้องใช้จากต่างประเทศ", url: `${SITE}/translate` },
              { name: "ยื่นสำนักงาน ตม.", text: `ค่าธรรมเนียมราชการ ${v.govFee} เสร็จ ${v.processingTime}`, url: `${SITE}/visa` },
            ],
          }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "Visa Checklist", item: `${SITE}/checklist` },
                { "@type": "ListItem", position: 3, name: v.nameTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: VisaChecklistHub,
});

function VisaChecklistHub() {
  const { id } = Route.useLoaderData();
  const v = getVisaType(id)!;
  const cat = CATEGORIES[v.category];

  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <nav aria-label="Breadcrumb" className="mb-3 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <ChevronRight className="mx-1 inline h-3 w-3 text-white/30" />
            <Link to="/checklist" className="hover:text-gold">Visa Checklist</Link>
            <ChevronRight className="mx-1 inline h-3 w-3 text-white/30" />
            <span className="text-white/85">{v.nameTh}</span>
          </nav>
          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${cat.color}`}>
            {cat.emoji} {cat.label} · {v.code}
          </span>
          <h1 className="aeo-speakable mt-3 font-display text-3xl font-extrabold leading-tight sm:text-4xl">
            Checklist เอกสาร <span className="text-gold">{v.nameTh}</span>
          </h1>
          <p className="mt-3 max-w-3xl text-base text-white/85">{v.tagline}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <h2 className="aeo-speakable mb-6 font-display text-2xl font-extrabold text-navy-deep">เลือกสถานการณ์ของคุณ</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {SCENARIOS.map((s) => {
            const ck = getChecklist(v.id, s.slug);
            return (
              <Link
                key={s.slug}
                to="/checklist/$visatype/$scenario"
                params={{ visatype: v.id, scenario: s.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="text-2xl">{s.emoji}</div>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
                </div>
                <div className="mt-2 font-display text-base font-extrabold text-navy-deep group-hover:text-amber-600">{s.nameTh}</div>
                <p className="mt-1 text-[13px] leading-relaxed text-slate-600">{s.description}</p>
                {ck && (
                  <div className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-700">
                    <FileCheck2 className="h-3 w-3" /> {ck.items.length} รายการเอกสาร
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
